import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Film, Plus, Calendar, Users, FileVideo, ChevronDown, Pencil, Loader2 } from "lucide-react";
import SessionBuilder from "./SessionBuilder";
import { CreateSessionForm } from "./SessionHighlights";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#D4AF37";
const VIS = { player: "Player only", team: "Team can see", public: "Public" };
const fmt = (d) => { const t = new Date(`${d}T12:00:00`); return isNaN(t) ? d : t.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }); };
const label = (p) => p.display_name || p.name || p.email;

const ShareStatus = ({ clip }) => {
  if (!clip.published) return null;
  if (clip.share_status === "failed") return <span data-testid={`cr-share-nocrest-${clip.id}`} className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#22C55E]"><span className="w-2 h-2 rounded-full bg-[#22C55E]" /> Ready to share · without crest</span>;
  if (clip.share_ready) return <span data-testid={`cr-share-ready-${clip.id}`} className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#22C55E]"><span className="w-2 h-2 rounded-full bg-[#22C55E]" /> Ready to share</span>;
  return <span data-testid={`cr-share-preparing-${clip.id}`} className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#D4AF37]"><Loader2 size={11} className="animate-spin" /> Preparing share clips…</span>;
};

const SessionDetail = ({ sid, auth, token }) => {
  const [s, setS] = useState(null);
  const load = useCallback(() => axios.get(`${API}/coach/highlight-sessions/${sid}`, auth).then(({ data }) => setS(data)).catch(() => setS({ players: [], clips: [] })), [sid, auth]);
  useEffect(() => { load(); }, [load]);
  const processing = s?.clips?.some((c) => c.published && !c.share_ready);
  useEffect(() => { if (!processing) return undefined; const t = setInterval(load, 5000); return () => clearInterval(t); }, [processing, load]);
  if (!s) return <div className="p-4 text-sm text-white/40">Loading…</div>;
  return (
    <div data-testid={`cr-detail-${sid}`} className="border-t border-white/10 divide-y divide-white/5">
      {s.players.map((p) => {
        const clips = s.clips.filter((c) => c.player_id === p.id);
        return (
          <div key={p.id} data-testid={`cr-player-${p.id}`} className="p-3 sm:p-4">
            <div className="font-headings uppercase tracking-wide">{p.name}</div>
            {clips.length === 0 ? <div className="text-xs text-white/40 mt-1">No clip uploaded.</div> : clips.map((c) => (
              <div key={c.id} data-testid={`cr-clip-${c.id}`} className="mt-2 flex gap-3">
                <video src={`${API}${c.video_url}?auth=${token}#t=0.1`} controls playsInline preload="metadata" className="w-28 sm:w-32 aspect-[9/16] object-cover rounded bg-black shrink-0" />
                <div className="min-w-0 space-y-1">
                  <div className="font-mono text-[11px] font-bold uppercase tracking-wider" style={{ color: GOLD }}>{c.move_label || "No move label"}</div>
                  <div className="text-sm text-white/70">{c.note || <span className="text-white/35">No coach note</span>}</div>
                  <div className="flex flex-wrap items-center gap-3"><span className="text-[10px] font-bold uppercase tracking-wider text-white/55">{VIS[c.visibility] || c.visibility}</span><ShareStatus clip={c} /></div>
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

const SessionRow = ({ s, open, onToggle, onEdit, auth, token }) => (
  <div data-testid={`cr-session-${s.id}`} className="rounded-lg border border-white/10 bg-[#0d0d0d] overflow-hidden">
    <div className="flex items-center gap-2 p-3 sm:p-4">
      <button data-testid={`cr-toggle-${s.id}`} onClick={onToggle} className="flex-1 min-w-0 text-left min-h-[44px]">
        <div className="flex items-center gap-2 flex-wrap"><span className="font-headings uppercase tracking-wide text-base truncate">{s.name}</span>
          <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${s.status === "published" ? "bg-[#22C55E]/15 text-[#22C55E]" : "bg-[#D4AF37]/15 text-[#D4AF37]"}`}>{s.status === "published" ? "Published" : "Draft"}</span></div>
        <div className="flex gap-3 mt-1 flex-wrap font-mono text-[10px] uppercase tracking-wider text-white/45">
          <span className="inline-flex items-center gap-1"><Calendar size={11} /> {fmt(s.date)}</span>
          <span data-testid={`cr-players-${s.id}`} className="inline-flex items-center gap-1"><Users size={11} /> {s.player_count} player{s.player_count === 1 ? "" : "s"}</span>
          <span data-testid={`cr-clips-${s.id}`} className="inline-flex items-center gap-1"><FileVideo size={11} /> {s.clip_count} clip{s.clip_count === 1 ? "" : "s"}</span>
        </div>
      </button>
      <button data-testid={`cr-edit-${s.id}`} onClick={onEdit} className="inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-sm border border-white/15 text-white/75 hover:text-white text-[11px] font-bold uppercase tracking-wider"><Pencil size={12} /> Edit Session</button>
      <button onClick={onToggle} aria-label="Expand session" className="w-11 h-11 flex items-center justify-center text-white/50"><ChevronDown size={18} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} /></button>
    </div>
    {open && <SessionDetail sid={s.id} auth={auth} token={token} />}
  </div>
);

export default function CoachRecaps({ token }) {
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [sessions, setSessions] = useState(null);
  const [roster, setRoster] = useState([]);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState("");
  const [editId, setEditId] = useState("");
  const load = useCallback(async () => {
    const [s, r] = await Promise.all([axios.get(`${API}/coach/highlight-sessions`, auth), axios.get(`${API}/coach/players`, auth)]);
    setSessions([...s.data.sessions].sort((a, b) => `${b.date}${b.created_at}`.localeCompare(`${a.date}${a.created_at}`)));
    setRoster([...r.data].sort((a, b) => label(a).localeCompare(label(b))));
  }, [auth]);
  useEffect(() => { load().catch(() => setSessions([])); }, [load]);
  if (editId) return <SessionBuilder token={token} sessionId={editId} onBack={() => { setEditId(""); load(); }} />;
  return (
    <section data-testid="coach-recaps" className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="font-mono text-[10px] font-bold uppercase tracking-[0.35em]" style={{ color: GOLD }}>Coach Hub</div>
          <h2 className="font-headings text-2xl sm:text-3xl uppercase tracking-wide flex items-center gap-2"><Film size={22} style={{ color: GOLD }} /> Session Recaps</h2>
          <p className="text-sm text-white/55 mt-1 max-w-xl">Every session, newest first. Expand one to check each player's clip, move, note and who can see it. Share clips get the Next O crest added automatically when you publish.</p>
        </div>
        {!creating && <button data-testid="cr-new-session" onClick={() => setCreating(true)} className="inline-flex items-center justify-center gap-2 min-h-[44px] px-5 rounded-sm bg-[#C41E24] text-white text-xs font-bold uppercase tracking-wider"><Plus size={14} /> New Session</button>}
      </div>
      {creating && <CreateSessionForm roster={roster} auth={auth} onCancel={() => setCreating(false)} onCreated={(s) => { setCreating(false); setEditId(s.id); }} />}
      {sessions === null ? <div className="text-white/40 text-sm">Loading sessions…</div> : sessions.length === 0 ? (
        <div data-testid="cr-empty" className="rounded-lg border border-dashed border-white/15 p-8 text-center text-sm text-white/45">No sessions yet — tap New Session to record your first one.</div>
      ) : (
        <div data-testid="cr-list" className="space-y-3">
          {sessions.map((s) => <SessionRow key={s.id} s={s} auth={auth} token={token} open={openId === s.id} onToggle={() => setOpenId(openId === s.id ? "" : s.id)} onEdit={() => setEditId(s.id)} />)}
        </div>
      )}
    </section>
  );
}
